import React, { useState } from 'react';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, Eye, RefreshCcw, XCircle } from 'lucide-react';
import { Button } from '../../Button';
import { Panel } from '../../common/Panel';
import { StatusBadge } from '../../common/StatusBadge';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { useApplicationReview } from '../../../contexts/ApplicationReviewContext';
import { can } from '../../../utils/applicationQueue';
import { ownerInfo, reviewDocs } from '../../../utils/applicationRecords';
import { toBn } from '../../../utils/bn';
import type { Tone } from '../../../types/domain';
import type { OwnerApplication } from '../../../types/owner';
import type { DocState, ReviewDoc } from '../../../types/review';
const stateMeta: Record<DocState, {
  label: string;
  tone: Tone;
}> = {
  pending: {
    label: 'পর্যালোচনা অপেক্ষমাণ',
    tone: 'gray'
  },
  verified: {
    label: 'যাচাইকৃত',
    tone: 'green'
  },
  unclear: {
    label: 'অস্পষ্ট',
    tone: 'orange'
  },
  incorrect: {
    label: 'ভুল ডকুমেন্ট',
    tone: 'red'
  },
  replace: {
    label: 'প্রতিস্থাপন অনুরোধ',
    tone: 'blue'
  }
};
const actionLabel: Record<Exclude<DocState, 'pending'>, string> = {
  verified: 'যাচাইকৃত হিসেবে চিহ্নিত',
  unclear: 'অস্পষ্ট হিসেবে চিহ্নিত',
  incorrect: 'ভুল হিসেবে চিহ্নিত',
  replace: 'প্রতিস্থাপনের অনুরোধ'
};
export function DocumentReviewTab({
  app
}: {
  app: OwnerApplication;
}) {
  const {
    registrations
  } = useOwnerService();
  const {
    getReview,
    updateReview,
    addHistory,
    role
  } = useApplicationReview();
  const [preview, setPreview] = useState<ReviewDoc | null>(null);
  const r = getReview(app);
  const docs = reviewDocs(app, ownerInfo(app, registrations).nid);
  const verified = docs.filter(d => r.docs[d.key] === 'verified').length;
  const editable = can(role, 'review') || can(role, 'validate');
  const mark = (d: ReviewDoc, s: Exclude<DocState, 'pending'>) => {
    updateReview(app, {
      docs: {
        ...r.docs,
        [d.key]: s
      }
    });
    addHistory(app, `ডকুমেন্ট — ${d.name}`, stateMeta[s].label, stateMeta[s].tone);
    toast.success(`${d.name}: ${actionLabel[s]}`);
    if (preview?.key === d.key) setPreview(null);
  };
  const actions = (d: ReviewDoc) => editable ? <div className="flex flex-wrap gap-1.5" data-id="element-340" data-mp="36h07kp8xhh5v">
        <Button size="sm" onClick={() => mark(d, 'verified')} className="bg-emerald-600 hover:bg-emerald-700" data-id="element-341" data-mp="36fp8h2qfgxl3"><CheckCircle2 className="h-3.5 w-3.5" data-id="element-342" data-mp="28ukppmclahhs" />যাচাইকৃত</Button>
        <Button size="sm" variant="outline" onClick={() => mark(d, 'unclear')} className="border-amber-300 text-amber-800" data-id="element-343" data-mp="2drtbdwwy53ns"><AlertCircle className="h-3.5 w-3.5" data-id="element-344" data-mp="2ir61cqj46qul" />অস্পষ্ট</Button>
        <Button size="sm" variant="outline" onClick={() => mark(d, 'incorrect')} className="border-red-300 text-red-700" data-id="element-345" data-mp="2ni5tlihagkqr"><XCircle className="h-3.5 w-3.5" data-id="element-346" data-mp="1q30arn5dq7mu" />ভুল</Button>
        <Button size="sm" variant="outline" onClick={() => mark(d, 'replace')} data-id="element-347" data-mp="1q2i745ge0r4w"><RefreshCcw className="h-3.5 w-3.5" data-id="element-348" data-mp="1v0ti4jif57op" />প্রতিস্থাপন অনুরোধ</Button>
      </div> : null;
  return <div className="space-y-4" data-id="element-349" data-mp="c5fk5tsf0h4b">
      <Panel title="সংযুক্ত ডকুমেন্ট যাচাই" subtitle={app.vehicleType === 'motorcycle' ? 'মোটরসাইকেলের প্রযোজ্য ডকুমেন্ট প্রদর্শিত' : 'সিএনজি/অটোরিকশার প্রয়োজনীয় ডকুমেন্ট'} data-id="element-350" data-mp="c4mjnak1unum">
        <div className="flex flex-wrap items-center gap-4" data-id="element-351" data-mp="34hc90euvpgg">
          <div className="flex-1" data-id="element-352" data-mp="3ucv9xzfixhlh">
            <p className="text-sm font-semibold text-navy-900" data-id="element-353" data-mp="3ubtvdkzaucz0">{toBn(docs.length)}টির মধ্যে {toBn(verified)}টি ডকুমেন্ট যাচাইকৃত</p>
            <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-100" data-id="element-354" data-mp="3pf3j78qm81iv">
              <span className="block h-full rounded-full bg-bdgreen-600 transition-all" style={{
              width: `${verified / docs.length * 100}%`
            }} data-id="element-355" data-mp="3kkfq0wko2ixy" />
            </div>
          </div>
          {editable && verified < docs.length && <Button variant="outline" size="sm" onClick={() => {
          updateReview(app, {
            docs: Object.fromEntries(docs.map(d => [d.key, 'verified']))
          });
          addHistory(app, 'সকল ডকুমেন্ট যাচাই সম্পন্ন', 'যাচাইকৃত', 'green');
        }} data-id="element-356" data-mp="36mhqgxb08r2i">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" data-id="element-357" data-mp="15k9vy2rh6en5" />সব যাচাইকৃত চিহ্নিত করুন
            </Button>}
        </div>
      </Panel>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-358" data-mp="1alphrx1h8p6l">
        {docs.map(d => {
        const st = stateMeta[r.docs[d.key] ?? 'pending'];
        return <article key={d.key} className="flex flex-col overflow-hidden rounded-lg border border-border bg-white shadow-sm" data-id="element-359" data-mp="10s8m0nhs500a">
              <button onClick={() => setPreview(d)} className="group relative h-32 bg-slate-100" aria-label={`${d.name} প্রিভিউ`} data-id="element-360" data-mp="mwekipjc5cx7">
                <img src={d.preview} alt="" className="h-full w-full object-cover" data-id="element-361" data-mp="cwdwwrefoan7" />
                <span className="absolute inset-0 flex items-center justify-center gap-1.5 bg-navy-900/0 text-sm font-semibold text-white opacity-0 transition group-hover:bg-navy-900/50 group-hover:opacity-100" data-id="element-362" data-mp="hup8b6kdmt7j"><Eye className="h-5 w-5" data-id="element-363" data-mp="2c9011q43e7il" />প্রিভিউ</span>
                <span className="absolute left-2 top-2 rounded bg-white/90 px-1.5 py-0.5 text-[10.5px] font-semibold text-slate-600" data-id="element-364" data-mp="2c8gh63uvt91x">{d.key === 'photo' ? 'JPG' : 'PDF/JPG'}</span>
              </button>
              <div className="flex flex-1 flex-col p-3.5" data-id="element-365" data-mp="3rtimg829hh86">
                <div className="mb-2 flex items-start justify-between gap-2" data-id="element-366" data-mp="aby0mphay0xs">
                  <h3 className="text-sm font-semibold text-navy-900" data-id="element-367" data-mp="5kprlujvwhl5">{d.name}</h3>
                  <StatusBadge tone={st.tone} data-id="element-368" data-mp="5iwm4il0c3eq">{st.label}</StatusBadge>
                </div>
                <dl className="mb-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs" data-id="element-369" data-mp="je7bxhke9yc8">
                  <div className="col-span-2" data-id="element-370" data-mp="f9zpjr1esuh6"><dt className="text-slate-400" data-id="element-371" data-mp="fcvif0yegsyr">ডকুমেন্ট নম্বর</dt><dd className="break-all font-mono text-navy-900" data-id="element-372" data-mp="t993ek257chz">{d.number}</dd></div>
                  <div data-id="element-373" data-mp="oaxtk9bul1zr"><dt className="text-slate-400" data-id="element-374" data-mp="oc8wk6xgy5pt">ইস্যুর তারিখ</dt><dd className="font-medium text-navy-900" data-id="element-375" data-mp="o94now926lst">{d.issue}</dd></div>
                  <div data-id="element-376" data-mp="2v1x9d1668e1g"><dt className="text-slate-400" data-id="element-377" data-mp="2v1x86xufq7zv">মেয়াদ শেষ</dt><dd className="font-medium text-navy-900" data-id="element-378" data-mp="2q33up5f5egzp">{d.expiry}</dd></div>
                </dl>
                <div className="mt-auto" data-id="element-379" data-mp="tzz3nqvya2q7">{actions(d)}</div>
              </div>
            </article>;
      })}
      </div>

      <DocumentPreviewModal doc={preview ? {
      name: preview.name,
      src: preview.preview,
      meta: [{
        label: 'ডকুমেন্ট নম্বর',
        value: preview.number
      }, {
        label: 'ইস্যুর তারিখ',
        value: preview.issue
      }, {
        label: 'মেয়াদ শেষের তারিখ',
        value: preview.expiry
      }, {
        label: 'পর্যালোচনা অবস্থা',
        value: stateMeta[r.docs[preview.key] ?? 'pending'].label
      }]
    } : null} onClose={() => setPreview(null)} actions={preview ? actions(preview) : undefined} data-id="element-380" data-mp="yzbqibmtn9ss" />
    </div>;
}